import React from 'react'
import styles from './StatisticsTable.module.sass'
import { StatisticsData, StatisticsRow } from '../../../../responses/statistics'
import { ColumnConfiguration, eqHide, HideConfiguration, OpenConfiguration, SortConfiguration } from './configuration'
import { ID } from '../../../../types/UtilityTypes'
import * as A from 'fp-ts/Array'
import { chop, difference, findFirst, spanLeft } from 'fp-ts/Array'
import StatisticsTableRow from './StatisticsTableRow'
import Button, {ButtonStyle} from '../../../controls/Button/Button'
import SortIndicator from './SortIndicator/SortIndicator'
import { head, NonEmptyArray, tail } from 'fp-ts/NonEmptyArray'
import { contramap, getDualOrd, ordString } from 'fp-ts/Ord'
import { identity, pipe } from 'fp-ts/function'
import * as O from 'fp-ts/Option'
import { SortDirection } from '../../../../constants/direction'
import { noop } from '../../../../utils/utils'
import { ordId } from '../../../../utils/entity-utils'

interface Props<T> {
    name: string,
    data: StatisticsData<T>
    config: NonEmptyArray<ColumnConfiguration<T>>,
    hide: HideConfiguration[],
    open: Record<ID, OpenConfiguration>,
    sort: SortConfiguration,
    onSortChange: (sort: SortConfiguration) => void,
    onRowToggle: (open: Record<ID, OpenConfiguration>) => void,
}

const groupGroups = chop<ColumnConfiguration<unknown>, Group>(columns => {
    const first = head(columns)
    const { init, rest } = spanLeft<ColumnConfiguration<unknown>>(
        column => column.group === first.group
    )(columns)
    return [{
        name: first.group,
        count: init.length
    }, rest]
})

interface Group {
    name: string,
    count: number,
}

const StatisticsTable = <T extends unknown>(props: Props<T>): JSX.Element => {
    const config = difference(eqHide)(tail(props.config), props.hide) as ColumnConfiguration<T>[]
    const total: StatisticsRow<T> = {
        name: 'Всего',
        total: props.data.total,
    }

    const ordRow = pipe(
        props.config,
        findFirst(column => eqHide.equals(column, props.sort)),
        O.chain(column => props.sort.direction === undefined?
            O.none:
            O.some(contramap(column.render)(ordId))
        ),
        O.getOrElse(() => contramap((row: StatisticsRow<T>) => row.name)(ordString)),
        props.sort.direction === SortDirection.DESC? getDualOrd: identity
    )

    return <div className={styles.table}>
        <table>
            <thead>
            <tr>
                <th/>
                {
                    groupGroups(config as ColumnConfiguration<unknown>[]).map(group => <th
                        key={group.name}
                        colSpan={group.count}
                    >
                        {group.name}
                    </th>)
                }
            </tr>
            <tr className={styles.total}>
                <th>
                    <Button
                        buttonStyle={ButtonStyle.TABLE_HEAD}
                        onClick={() => props.onSortChange({
                        group: '',
                        name: '',
                        direction:
                            props.sort.group === ''?
                                props.sort.direction === SortDirection.ASC?
                                    SortDirection.DESC:
                                    SortDirection.ASC:
                                SortDirection.ASC
                    })}>
                        {props.name}
                        <SortIndicator value={props.sort.group === ''? props.sort.direction: undefined}/>
                    </Button>
                </th>
                {
                    config.map(column => {
                        const direction = eqHide.equals(props.sort, column) ?
                            props.sort.direction :
                            undefined
                        return <th key={`${column.group}-${column.name}`}>
                            <Button
                                buttonStyle={ButtonStyle.TABLE_SMALL_HEAD}
                                onClick={() => props.onSortChange({
                                group: column.group,
                                name: column.name,
                                direction: direction === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC
                            })}>
                                {column.name}
                                <SortIndicator value={direction}/>
                            </Button>
                        </th>
                    })
                }
            </tr>
            </thead>
            <tbody>
            <StatisticsTableRow data={total} config={config} ord={ordRow} level={0} onToggle={noop}/>
            {
                pipe(
                    props.data.rows ?? [],
                    A.sort(ordRow),
                    A.mapWithIndex((index, row) => <StatisticsTableRow
                        key={index}
                        data={row}
                        open={props.open[index]}
                        config={config}
                        ord={ordRow}
                        level={0}
                        onToggle={open => props.onRowToggle({
                            ...props.open,
                            [index]: open,
                        })}
                    />)
                )
            }
            </tbody>
        </table>
    </div>
}

export default StatisticsTable